import React, { useCallback, useEffect, useRef, useState } from "react"
import { $isCodeHighlightNode } from "@lexical/code"
import { $isLinkNode, TOGGLE_LINK_COMMAND } from "@lexical/link"
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext"
import { $getSelectionStyleValueForProperty, $patchStyleText } from "@lexical/selection"
import { Popover, PopoverContent, PopoverTrigger } from "@ui/shad"
import {
  $getSelection,
  $isParagraphNode,
  $isRangeSelection,
  $isTextNode,
  $setSelection,
  BaseSelection,
  COMMAND_PRIORITY_LOW,
  FORMAT_TEXT_COMMAND,
  getDOMSelection,
  LexicalEditor,
  SELECTION_CHANGE_COMMAND,
} from "lexical"
import { BoldIcon, ItalicIcon, Link2Icon, Link2OffIcon, PaintbrushVerticalIcon, UnderlineIcon } from "lucide-react"
import { createPortal } from "react-dom"

import { COLOR_PRESETS } from "@/components/lexical/color-presets"
import { OPEN_FLOATING_LINK_EDITOR_COMMAND } from "@/components/lexical/plugins/floating-link-editor"

import mergeRegister, { getDOMRangeRect, getSelectedNode, setFloatingElemPosition } from "./utils"

function ColorPickerButton({ editor, currentColor }: { editor: LexicalEditor; currentColor: string }) {
  const savedSelectionRef = useRef<BaseSelection | null>(null)

  const saveSelection = () => {
    editor.getEditorState().read(() => {
      const selection = $getSelection()
      if ($isRangeSelection(selection)) {
        savedSelectionRef.current = selection.clone()
      }
    })
  }

  const applyColor = (color: string) => {
    if (!savedSelectionRef.current) return
    editor.focus()
    editor.update(() => {
      $setSelection(savedSelectionRef.current!.clone())
      const selection = $getSelection()
      if ($isRangeSelection(selection)) {
        $patchStyleText(selection, { color: color || "" })
      }
    })
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="hover:bg-tertiary m-0.5 flex h-8 w-8 cursor-pointer items-center justify-center rounded-sm border-0 bg-transparent"
          title="Text color"
          aria-label="Change text color"
          onPointerDown={saveSelection}
        >
          <PaintbrushVerticalIcon
            className="text-icon size-3"
            style={currentColor ? { color: currentColor } : undefined}
          />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-2" side="bottom" align="start">
        <div className="flex flex-col gap-1">
          {COLOR_PRESETS.map((preset) => (
            <button
              key={preset.value}
              type="button"
              className={`hover:bg-tertiary flex items-center gap-2 rounded px-2 py-1 text-sm ${
                (preset.value === "" && !currentColor) || preset.value === currentColor ? "bg-blue-background" : ""
              }`}
              onClick={() => applyColor(preset.value)}
            >
              <span
                className="size-4 shrink-0 rounded-full"
                style={{ backgroundColor: preset.value || "var(--foreground)" }}
              />
              <span className="text-foreground">{preset.label}</span>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  )
}

function MenuOption({
  onClick,
  isActive,
  title,
  ariaLabel,
  icon,
}: {
  onClick: () => void
  isActive: boolean
  title: string
  ariaLabel: string
  icon: React.JSX.Element
}): React.JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`m-0.5 flex h-8 w-8 cursor-pointer items-center justify-center rounded-sm border-0 bg-transparent ${
        isActive ? "bg-blue-background" : ""
      } hover:bg-tertiary`}
      title={title}
      aria-label={ariaLabel}
    >
      <span className="flex w-3">{icon}</span>
    </button>
  )
}

function TextFormatFloatingToolbar({
  editor,
  anchorElem,
  isBold,
  isItalic,
  isUnderline,
  isLink,
  currentColor,
  onEditLink,
  onRemoveLink,
}: {
  editor: LexicalEditor
  anchorElem: HTMLElement
  isBold: boolean
  isItalic: boolean
  isUnderline: boolean
  isLink: boolean
  currentColor: string
  onEditLink: () => void
  onRemoveLink: () => void
}): React.JSX.Element {
  const popupCharStylesEditorRef = useRef<HTMLDivElement | null>(null)

  function mouseMoveListener(e: MouseEvent) {
    if (popupCharStylesEditorRef?.current && (e.buttons === 1 || e.buttons === 3)) {
      if (popupCharStylesEditorRef.current.style.pointerEvents !== "none") {
        const x = e.clientX
        const y = e.clientY
        const elementUnderMouse = document.elementFromPoint(x, y)

        if (!popupCharStylesEditorRef.current.contains(elementUnderMouse)) {
          popupCharStylesEditorRef.current.style.pointerEvents = "none"
        }
      }
    }
  }

  function mouseUpListener() {
    if (popupCharStylesEditorRef?.current) {
      if (popupCharStylesEditorRef.current.style.pointerEvents !== "auto") {
        popupCharStylesEditorRef.current.style.pointerEvents = "auto"
      }
    }
  }

  useEffect(() => {
    if (popupCharStylesEditorRef?.current) {
      document.addEventListener("mousemove", mouseMoveListener)
      document.addEventListener("mouseup", mouseUpListener)

      return () => {
        document.removeEventListener("mousemove", mouseMoveListener)
        document.removeEventListener("mouseup", mouseUpListener)
      }
    }
  }, [popupCharStylesEditorRef])

  const $updateTextFormatFloatingToolbar = useCallback(() => {
    const selection = $getSelection()
    const popupCharStylesEditorElem = popupCharStylesEditorRef.current
    const nativeSelection = getDOMSelection(editor._window)

    if (popupCharStylesEditorElem === null) {
      return
    }

    const rootElement = editor.getRootElement()
    if (
      selection !== null &&
      nativeSelection !== null &&
      !nativeSelection.isCollapsed &&
      rootElement !== null &&
      rootElement.contains(nativeSelection.anchorNode)
    ) {
      const rangeRect = getDOMRangeRect(nativeSelection, rootElement)
      setFloatingElemPosition(rangeRect, popupCharStylesEditorElem, anchorElem)
    }
  }, [editor, anchorElem])

  useEffect(() => {
    const scrollerElem = anchorElem.parentElement

    const update = () => {
      editor.getEditorState().read(() => {
        $updateTextFormatFloatingToolbar()
      })
    }

    window.addEventListener("resize", update)
    if (scrollerElem) {
      scrollerElem.addEventListener("scroll", update)
    }

    return () => {
      window.removeEventListener("resize", update)
      if (scrollerElem) {
        scrollerElem.removeEventListener("scroll", update)
      }
    }
  }, [editor, $updateTextFormatFloatingToolbar, anchorElem])

  useEffect(() => {
    editor.getEditorState().read(() => {
      $updateTextFormatFloatingToolbar()
    })
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          $updateTextFormatFloatingToolbar()
        })
      }),

      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          $updateTextFormatFloatingToolbar()
          return false
        },
        COMMAND_PRIORITY_LOW
      )
    )
  }, [editor, $updateTextFormatFloatingToolbar])

  return (
    <div
      ref={popupCharStylesEditorRef}
      className="border-border-accent bg-background absolute top-0 left-0 z-10 flex h-10 items-center rounded-sm border p-1 opacity-0 shadow-lg transition-opacity duration-300 will-change-transform"
    >
      {editor.isEditable() && (
        <>
          <MenuOption
            onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "bold")}
            isActive={isBold}
            title="Bold"
            ariaLabel="Format text as bold"
            icon={<BoldIcon />}
          />
          <MenuOption
            onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "italic")}
            isActive={isItalic}
            title="Italic"
            ariaLabel="Format text as italics"
            icon={<ItalicIcon />}
          />
          <MenuOption
            onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "underline")}
            isActive={isUnderline}
            title="Underline"
            ariaLabel="Format text to be underlined"
            icon={<UnderlineIcon />}
          />
          <ColorPickerButton editor={editor} currentColor={currentColor} />
          <MenuOption
            onClick={onEditLink}
            isActive={isLink}
            title={isLink ? "Edit link" : "Insert link"}
            ariaLabel={isLink ? "Edit selected link" : "Insert link"}
            icon={<Link2Icon />}
          />
          {isLink && (
            <MenuOption
              onClick={onRemoveLink}
              isActive={false}
              title="Remove link"
              ariaLabel="Remove selected link"
              icon={<Link2OffIcon />}
            />
          )}
        </>
      )}
    </div>
  )
}

function useFloatingTextFormatToolbar(editor: LexicalEditor, anchorElem: HTMLElement): React.JSX.Element | null {
  const [isText, setIsText] = useState(false)
  const [isBold, setIsBold] = useState(false)
  const [isItalic, setIsItalic] = useState(false)
  const [isUnderline, setIsUnderline] = useState(false)
  const [isLink, setIsLink] = useState(false)
  const [currentColor, setCurrentColor] = useState("")

  const handleEditLink = useCallback(() => {
    editor.dispatchCommand(OPEN_FLOATING_LINK_EDITOR_COMMAND, undefined)
  }, [editor])

  const handleRemoveLink = useCallback(() => {
    editor.dispatchCommand(TOGGLE_LINK_COMMAND, null)
  }, [editor])

  const updatePopup = useCallback(() => {
    editor.getEditorState().read(() => {
      if (editor.isComposing()) {
        return
      }
      const selection = $getSelection()
      const nativeSelection = getDOMSelection(editor._window)
      const rootElement = editor.getRootElement()

      if (
        nativeSelection !== null &&
        (!$isRangeSelection(selection) || rootElement === null || !rootElement.contains(nativeSelection.anchorNode))
      ) {
        setIsText(false)
        return
      }

      if (!$isRangeSelection(selection)) {
        return
      }

      const node = getSelectedNode(selection)

      setIsBold(selection.hasFormat("bold"))
      setIsItalic(selection.hasFormat("italic"))
      setIsUnderline(selection.hasFormat("underline"))
      setCurrentColor($getSelectionStyleValueForProperty(selection, "color", ""))
      const parent = node.getParent()
      setIsLink($isLinkNode(node) || $isLinkNode(parent))

      if (!$isCodeHighlightNode(selection.anchor.getNode()) && selection.getTextContent() !== "") {
        setIsText($isTextNode(node) || $isParagraphNode(node))
      } else {
        setIsText(false)
      }

      const rawTextContent = selection.getTextContent().replace(/\n/g, "")
      if (!selection.isCollapsed() && rawTextContent === "") {
        setIsText(false)
        return
      }
    })
  }, [editor])

  useEffect(() => {
    document.addEventListener("selectionchange", updatePopup)
    return () => {
      document.removeEventListener("selectionchange", updatePopup)
    }
  }, [updatePopup])

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(() => {
        updatePopup()
      }),
      editor.registerRootListener(() => {
        if (editor.getRootElement() === null) {
          setIsText(false)
        }
      })
    )
  }, [editor, updatePopup])

  if (!isText || isLink) {
    return null
  }

  return createPortal(
    <TextFormatFloatingToolbar
      editor={editor}
      anchorElem={anchorElem}
      isBold={isBold}
      isItalic={isItalic}
      isUnderline={isUnderline}
      isLink={isLink}
      currentColor={currentColor}
      onEditLink={handleEditLink}
      onRemoveLink={handleRemoveLink}
    />,
    anchorElem
  )
}

export default function FloatingTextFormatToolbarPlugin({
  anchorElem = document.body,
}: {
  anchorElem?: HTMLElement
}): React.JSX.Element | null {
  const [editor] = useLexicalComposerContext()
  return useFloatingTextFormatToolbar(editor, anchorElem)
}
